import type { Duration } from '@fullcalendar/core';
import { DateTime as DT } from 'luxon';
import type { TaskType } from '@econtractor/types';
import { millisToTime } from '../../NewTask/helpers/initNewTaskStruct';
import {
    dueDatesFromTask,
    normalizeDueDateValues,
    type TaskDueDateValues,
} from '../../shared/helpers/taskDueDateHelpers';

export type NormalizedTaskDueDates = ReturnType<typeof normalizeDueDateValues>;

const shiftMillis = (millis: number, delta: Duration, ignoreTime: boolean): number | null => {
    const dt = DT.fromMillis(millis, { zone: 'local' });
    if (!dt.isValid) return null;
    const next = dt.plus({
        years: delta.years || 0,
        months: delta.months || 0,
        days: delta.days || 0,
        milliseconds: ignoreTime ? 0 : (delta.milliseconds || 0),
    });
    return next.isValid ? next.toMillis() : null;
};

export const isZeroCalendarDelta = (delta: Duration): boolean =>
    !delta.years && !delta.months && !delta.days && !delta.milliseconds;

export const applyCalendarEventDrop = (
    task: TaskType,
    delta: Duration,
): NormalizedTaskDueDates | null => {
    const current: TaskDueDateValues = dueDatesFromTask(task);
    const ignoreTime = current.multiDay || !current.withTime;
    const dateEnd = shiftMillis(current.dateEnd, delta, ignoreTime);
    if (dateEnd == null) return null;

    const dateStart = shiftMillis(current.dateStart, delta, ignoreTime);
    if (dateStart == null) return null;

    return normalizeDueDateValues({
        ...current,
        dateStart,
        dateEnd,
        startTime: current.withTime ? millisToTime(dateStart, '00:00') : current.startTime,
        endTime: current.withTime ? millisToTime(dateEnd) : current.endTime,
    });
};

export const isSameNormalizedDue = (
    a: NormalizedTaskDueDates,
    b: NormalizedTaskDueDates,
): boolean =>
    a.dateStart === b.dateStart
    && a.dateEnd === b.dateEnd
    && a.multiDay === b.multiDay
    && a.withTime === b.withTime
    && a.endTime === b.endTime;
